const getBaseUrl = (): string => {
  const url = import.meta.env.VITE_BACKEND_URL;
  if (url && typeof url === 'string' && url.trim() !== '') return url.replace(/\/$/, '');
  return 'http://localhost:3001';
};

let tokenGetter: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: (() => Promise<string | null>) | null) {
  tokenGetter = getter;
}

async function fetchJson<T>(path: string, options?: RequestInit): Promise<T> {
  const base = getBaseUrl();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options?.headers as Record<string, string>) ?? {}),
  };
  if (tokenGetter) {
    const t = await tokenGetter();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  const res = await fetch(`${base}${path}`, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AdminMe {
  uid: string;
  email: string | null;
  roles: string[];
}

export interface ActivityStats {
  totalUsers: number;
  activeUsersLast7Days: number;
  totalLessonCompletions: number;
  completionsLast7Days: number;
  proSubscribers: number;
  achievementsUnlocked: number;
}

export interface RecentCompletion {
  userId: string;
  userDisplayName: string | null;
  userEmail: string | null;
  lessonId: string;
  lessonTitle: string;
  trackName: string;
  completedAt: string;
  xpEarned: number;
}

export async function adminMe(): Promise<AdminMe> {
  return fetchJson<AdminMe>('/admin/auth/me');
}

export interface Pathway {
  id: string;
  name: string;
  order: number;
  icon: string | null;
}

export interface Track {
  id: string;
  name: string;
  description: string;
  icon: string;
  pathway: { id: string; name: string };
  difficulty: string;
  color: string;
  lessons?: { id: string; title: string; description: string; icon: string }[];
}

/** Payload for PATCH /admin/tracks/:id (backend accepts pathwayId, not pathway). */
export interface TrackUpdateDto {
  name?: string;
  description?: string;
  icon?: string;
  pathwayId?: string;
  difficulty?: string;
  color?: string;
  order?: number;
}

export interface Lesson {
  id: string;
  title: string;
  description: string;
  icon: string;
  trackId?: string;
  order?: number;
  isPro?: boolean;
}

export interface Question {
  id: string;
  type: string;
  prompt: string;
  options?: string[];
  correctAnswer: string | string[];
  explanation: string;
  hint?: string | null;
}

export type RequirementTypeValue =
  | 'lesson_count'
  | 'track_complete'
  | 'streak_days'
  | 'xp_total';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  requirement: string;
  requirementType: RequirementTypeValue;
  requirementConfig: Record<string, unknown>;
  reward: number;
}

export interface GeneratedTrack {
  id: string;
  name: string;
  description: string;
  icon?: string;
  pathway: string; // name, resolve to pathwayId on create
  difficulty: string;
  color: string;
  lessons: Array<{ id: string; title: string; description: string; icon: string; order: number }>;
}

export interface GeneratedLesson {
  id: string;
  title: string;
  description: string;
  icon: string;
  trackId?: string;
  order?: number;
}

export interface ArticleSource {
  id: string;
  name: string;
  feedUrl: string | null;
  baseUrl: string | null;
  isActive: boolean;
  order: number;
  _count?: { articles: number };
}

export interface SyncSummaryCard {
  id: string;
  title: string;
  sub_title: string;
  /** Backend may return camelCase */
  subTitle?: string;
  sections: Array<{ type: 'text' | 'image'; content: string }>;
  key_takeaway?: string | null;
  order: number;
}

export interface SyncJobRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  processed: number;
  skipped: number;
  errors: { url: string; message: string }[] | null;
  details: Record<string, unknown> | null;
}

export interface SyncArticle {
  id: string;
  articleSourceId: string;
  articleTitle: string;
  sourceUrl: string;
  thumbnailUrl: string | null;
  publishedAt: string | null;
  published?: boolean;
  articleSource?: { name: string };
  summaryCards?: SyncSummaryCard[];
  _count?: { summaryCards: number };
}

export interface GeneratedSyncArticle {
  articleSourceId: string;
  articleTitle: string;
  sourceUrl: string;
  thumbnailUrl?: string;
  publishedAt?: string;
  summaryCards: Array<{
    title: string;
    sub_title: string;
    sections: Array<{ type: 'text' | 'image'; content: string }>;
    key_takeaway?: string;
  }>;
}

export interface ContentExportPathway {
  id: string;
  name: string;
  order: number;
  icon: string | null;
}

export interface ContentExport {
  version: number;
  exportedAt: string;
  pathways: ContentExportPathway[];
  tracks: ContentExportTrack[];
  achievements: Achievement[];
}

export interface ContentExportTrack {
  id: string;
  name: string;
  description: string;
  icon: string;
  pathwayId: string;
  pathwayName: string;
  difficulty: string;
  color: string;
  order: number;
  lessons: Array<{
    id: string;
    trackId: string;
    title: string;
    description: string;
    icon: string;
    order: number;
    questions?: Question[];
  }>;
}

export const adminApi = {
  me: adminMe,

  ai: {
    generateTrack: (prompt: string) =>
      fetchJson<GeneratedTrack>('/admin/ai/generate-track', { method: 'POST', body: JSON.stringify({ prompt }) }),
    generateLesson: (prompt: string, trackId?: string) =>
      fetchJson<GeneratedLesson>('/admin/ai/generate-lesson', {
        method: 'POST',
        body: JSON.stringify({ prompt, trackId }),
      }),
    generateQuestions: (lessonTitle: string, lessonDescription: string, count?: number) =>
      fetchJson<Question[]>('/admin/ai/generate-questions', {
        method: 'POST',
        body: JSON.stringify({ lessonTitle, lessonDescription, count: count ?? 3 }),
      }),
    generateSync: (body: { articleSourceId: string; sourceUrl: string; articleContent?: string }) =>
      fetchJson<GeneratedSyncArticle>('/admin/ai/generate-sync', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  articleSources: {
    list: () => fetchJson<ArticleSource[]>('/admin/article-sources'),
    get: (id: string) => fetchJson<ArticleSource>(`/admin/article-sources/${id}`),
    create: (dto: { name: string; feedUrl?: string | null; baseUrl?: string | null; isActive?: boolean; order?: number }) =>
      fetchJson<ArticleSource>('/admin/article-sources', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: string, dto: Partial<ArticleSource>) =>
      fetchJson<ArticleSource>(`/admin/article-sources/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    delete: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/article-sources/${id}`, { method: 'DELETE' }),
    reorder: (ids: string[]) =>
      fetchJson<{ ok: boolean }>('/admin/article-sources/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
  },

  syncArticles: {
    list: (sourceId?: string) =>
      fetchJson<SyncArticle[]>(sourceId ? `/admin/sync-articles?sourceId=${encodeURIComponent(sourceId)}` : '/admin/sync-articles'),
    get: (id: string) => fetchJson<SyncArticle>(`/admin/sync-articles/${id}`),
    create: (dto: {
      articleSourceId: string;
      articleTitle: string;
      sourceUrl: string;
      thumbnailUrl?: string | null;
      publishedAt?: string | null;
      published?: boolean;
      summaryCards: Array<{
        title: string;
        sub_title: string;
        sections: Array<{ type: 'text' | 'image'; content: string }>;
        key_takeaway?: string | null;
        order?: number;
      }>;
    }) =>
      fetchJson<SyncArticle>('/admin/sync-articles', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: string, dto: { articleSourceId?: string; articleTitle?: string; sourceUrl?: string; thumbnailUrl?: string | null; publishedAt?: string | null; published?: boolean }) =>
      fetchJson<SyncArticle>(`/admin/sync-articles/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    delete: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/sync-articles/${id}`, { method: 'DELETE' }),
    createCard: (articleId: string, dto: { title: string; sub_title: string; sections: Array<{ type: 'text' | 'image'; content: string }>; key_takeaway?: string | null; order?: number }) =>
      fetchJson<SyncSummaryCard>(`/admin/sync-articles/${articleId}/cards`, { method: 'POST', body: JSON.stringify(dto) }),
    updateCard: (articleId: string, cardId: string, dto: Partial<{ title: string; sub_title: string; sections: Array<{ type: 'text' | 'image'; content: string }>; key_takeaway?: string | null; order?: number }>) =>
      fetchJson<SyncSummaryCard>(`/admin/sync-articles/${articleId}/cards/${cardId}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    deleteCard: (articleId: string, cardId: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/sync-articles/${articleId}/cards/${cardId}`, { method: 'DELETE' }),
    reorderCards: (articleId: string, ids: string[]) =>
      fetchJson<{ ok: boolean }>(`/admin/sync-articles/${articleId}/reorder-cards`, { method: 'POST', body: JSON.stringify({ ids }) }),
  },

  syncJobs: {
    listRuns: () => fetchJson<SyncJobRun[]>('/admin/sync-jobs/runs'),
    getRun: (id: string) => fetchJson<SyncJobRun>(`/admin/sync-jobs/runs/${id}`),
    trigger: () =>
      fetchJson<{ runId: string; processed: number; skipped: number; errors: { url: string; message: string }[] }>(
        '/admin/sync-jobs/trigger',
        { method: 'POST' },
      ),
  },

  pathways: {
    list: () => fetchJson<Pathway[]>('/admin/pathways'),
    get: (id: string) => fetchJson<Pathway>(`/admin/pathways/${id}`),
    create: (dto: { id: string; name: string; order?: number; icon?: string | null }) =>
      fetchJson<Pathway>('/admin/pathways', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: string, dto: Partial<Pathway>) =>
      fetchJson<Pathway>(`/admin/pathways/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    delete: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/pathways/${id}`, { method: 'DELETE' }),
    reorder: (ids: string[]) =>
      fetchJson<{ ok: boolean }>('/admin/pathways/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
  },

  content: {
    export: () => fetchJson<ContentExport>('/admin/content/export'),
    import: (body: { pathways?: ContentExportPathway[]; tracks: ContentExportTrack[]; achievements?: Achievement[] }) =>
      fetchJson<{ pathwaysCreated: number; tracksCreated: number; lessonsCreated: number; questionsCreated: number; achievementsCreated: number }>(
        '/admin/content/import',
        { method: 'POST', body: JSON.stringify(body) },
      ),
  },

  tracks: {
    list: () => fetchJson<Track[]>('/admin/tracks'),
    get: (id: string) => fetchJson<Track>(`/admin/tracks/${id}`),
    create: (dto: Partial<Track> & { id: string; name: string; description: string; icon: string; pathwayId: string; difficulty: string; color: string }) =>
      fetchJson<Track>('/admin/tracks', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: string, dto: TrackUpdateDto) =>
      fetchJson<Track>(`/admin/tracks/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    delete: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/tracks/${id}`, { method: 'DELETE' }),
    reorder: (ids: string[]) =>
      fetchJson<{ ok: boolean }>('/admin/tracks/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
  },

  lessons: {
    list: (trackId?: string) =>
      fetchJson<Lesson[]>(trackId ? `/admin/lessons?trackId=${encodeURIComponent(trackId)}` : '/admin/lessons'),
    get: (id: string) => fetchJson<Lesson>(`/admin/lessons/${id}`),
    create: (dto: { id: string; trackId: string; title: string; description: string; icon: string; order?: number; isPro?: boolean }) =>
      fetchJson<Lesson>('/admin/lessons', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: string, dto: Partial<Lesson>) =>
      fetchJson<Lesson>(`/admin/lessons/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    delete: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/lessons/${id}`, { method: 'DELETE' }),
    questions: (lessonId: string) => fetchJson<Question[]>(`/admin/lessons/${lessonId}/questions`),
    createQuestion: (lessonId: string, dto: Partial<Question> & { type: string; prompt: string; explanation: string; correctAnswer: string | string[] }) =>
      fetchJson<Question>(`/admin/lessons/${lessonId}/questions`, { method: 'POST', body: JSON.stringify(dto) }),
    reorderQuestions: (lessonId: string, ids: string[]) =>
      fetchJson<{ ok: boolean }>(`/admin/lessons/${lessonId}/questions/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),
    reorder: (trackId: string, ids: string[]) =>
      fetchJson<{ ok: boolean }>('/admin/lessons/reorder', { method: 'POST', body: JSON.stringify({ trackId, ids }) }),
  },

  questions: {
    update: (id: string, dto: Partial<Question>) =>
      fetchJson<Question>(`/admin/questions/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    delete: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/questions/${id}`, { method: 'DELETE' }),
  },

  activity: {
    stats: (from?: string) => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      return fetchJson<ActivityStats>(`/admin/activity/stats${params.toString() ? `?${params}` : ''}`);
    },
    recentCompletions: (limit?: number, from?: string) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (from) params.set('from', from);
      return fetchJson<RecentCompletion[]>(`/admin/activity/recent-completions${params.toString() ? `?${params}` : ''}`);
    },
  },

  achievements: {
    list: () => fetchJson<Achievement[]>('/admin/achievements'),
    get: (id: string) => fetchJson<Achievement>(`/admin/achievements/${id}`),
    create: (dto: Achievement) =>
      fetchJson<Achievement>('/admin/achievements', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: string, dto: Partial<Achievement>) =>
      fetchJson<Achievement>(`/admin/achievements/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    delete: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/admin/achievements/${id}`, { method: 'DELETE' }),
  },
};
